import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { api, mediaUrl, errMsg, triggerDownload } from "../lib/api";
import { useAuth } from "./AuthContext";
import { useT } from "../lib/i18n";
import { useLiveStats } from "./LiveStats";
import * as engine from "../lib/audioEngine";
import { useMediaSession } from "../lib/useMediaSession";



const PlayerCtx = createContext(null);

export const PlayerProvider = ({ children }) => {
  const { user, requireUser } = useAuth();
  const { t } = useT();
  const audioRef = useRef(null);
  const graphRef = useRef(null);
  const [mix, setMix] = useState(null);
  const [queue, setQueue] = useState([]);
  const [playing, setPlaying] = useState(false);
  const [time, setTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(0.85);
  const [fxMaster, setFxMaster] = useState(0.8);
  const [fxVol, setFxVol] = useState({});
  const [fxOpen, setFxOpen] = useState(false);
  const [fxMode, setFxMode] = useState("shot");
  const [shareMix, setShareMix] = useState(null);
  const [overrides, setOverrides] = useState({});
  const { counts: live, refresh: refreshLive } = useLiveStats();

  useEffect(() => {
    const a = new Audio();
    a.preload = "metadata";
    a.crossOrigin = "use-credentials";
    audioRef.current = a;
    const onTime = () => setTime(a.currentTime);
    const onMeta = () => setDuration(a.duration);
    const onPlay = () => setPlaying(true);
    const onPause = () => setPlaying(false);
    a.addEventListener("timeupdate", onTime);
    a.addEventListener("loadedmetadata", onMeta);
    a.addEventListener("play", onPlay);
    a.addEventListener("pause", onPause);
    return () => {
      a.pause();
      a.removeEventListener("timeupdate", onTime);
      a.removeEventListener("loadedmetadata", onMeta);
      a.removeEventListener("play", onPlay);
      a.removeEventListener("pause", onPause);
    };
  }, []);

  useEffect(() => {
    if (audioRef.current) audioRef.current.volume = volume;
  }, [volume]);

  useEffect(() => { engine.setMaster(fxMaster); }, [fxMaster]);
  useEffect(() => { Object.entries(fxVol).forEach(([id, v]) => engine.setPadVolume(id, v)); }, [fxVol]);

  const patch = useCallback((id, data) => setOverrides((o) => ({ ...o, [id]: { ...(o[id] || {}), ...data } })), []);
  const withOverrides = useCallback((m) => (m ? { ...m, ...(overrides[m.id] || {}), ...(live.mixes[m.id] || {}) } : m), [overrides, live]);

  const routedRef = useRef(false);
  const stateRef = useRef({ bpm: 140, playing: false });
  const ensureGraph = (route = !engine.isIOS()) => {
    if (!graphRef.current) {
      graphRef.current = { analyser: engine.fakeAnalyser(() => stateRef.current.bpm, () => stateRef.current.playing) };
    }
    if (!route || routedRef.current) { if (routedRef.current) engine.getCtx(); return; }
    engine.getCtx();
    routedRef.current = true;
    graphRef.current.analyser = engine.getAnalyser();
    engine.attachMedia(audioRef.current).catch(() => {});
  };

  const playMix = useCallback((m, list) => {
    const a = audioRef.current;
    if (list) setQueue(list);
    if (mix?.id === m.id) {
      ensureGraph();
      if (a.paused) a.play().catch(() => {});
      else a.pause();
      return;
    }
    setMix(m);
    setTime(0);
    a.src = mediaUrl(m.audio_file_id);
    ensureGraph();
    a.play().catch(() => toast.error(t("toast.playFail")));
    api.post(`/mixes/${m.id}/play`).then((r) => { patch(m.id, { plays: r.data.plays }); refreshLive(); }).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mix, patch]);

  const toggle = () => mix && playMix(mix);
  const seek = (t) => { if (audioRef.current) audioRef.current.currentTime = t; };
  const step = async (dir) => {
    if (!mix) return;
    let list = queue;
    if (list.length < 2) {
      const r = await api.get("/mixes").catch(() => null);
      list = Array.isArray(r?.data) ? r.data : list;
      setQueue(list);
    }
    const i = list.findIndex((q) => q.id === mix.id);
    const next = list[(i + dir + list.length) % list.length];
    if (next && next.id !== mix.id) playMix(next);
  };
  const stepRef = useRef(step);
  stepRef.current = step;
  useEffect(() => {
    const a = audioRef.current;
    const onEnd = () => stepRef.current(1);
    a.addEventListener("ended", onEnd);
    return () => a.removeEventListener("ended", onEnd);
  }, []);

  const downloadMix = async (m) => {
    if (!requireUser()) return;
    try {
      const r = await api.post(`/mixes/${m.id}/download`);
      patch(m.id, { downloads: r.data.downloads });
      refreshLive();
      triggerDownload(r.data.url);
      toast.success(`${t("toast.downloading")} "${m.title}"`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  const likeMix = async (m) => {
    if (!requireUser()) return;
    try {
      const r = await api.post(`/mixes/${m.id}/like`);
      patch(m.id, { liked: r.data.liked, likes: r.data.likes });
      refreshLive();
    } catch (e) { toast.error(errMsg(e)); }
  };

  const beat = 60 / (mix?.bpm > 40 ? mix.bpm : 140);
  const slice = beat / 2;

  const fxPress = (fx) => {
    engine.padDown(fx.id, mediaUrl(fx.file_id), { mode: fxMode, slice, beat }).catch(() => toast.error(t("toast.fxFail")));
    api.post(`/fx/${fx.id}/hit`).then(refreshLive).catch(() => {});
  };
  const fxRelease = (fx) => engine.padUp(fx.id, fxMode);

  const mixFx = {
    echo: (on) => { ensureGraph(true); engine.setEcho(on, beat); },
    reverb: (on) => { ensureGraph(true); engine.setReverb(on); },
  };
  stateRef.current = { bpm: 60 / beat, playing };

  const curMix = withOverrides(mix);
  useMediaSession(curMix, { audioRef, toggle, step, seek, time, duration });

  return (
    <PlayerCtx.Provider value={{
      user, audioRef, graphRef, mix: curMix, queue, playing, time, duration, volume, setVolume, playMix, toggle, seek, step,
      downloadMix, likeMix, shareMix, setShareMix, withOverrides, patch, fxMaster, setFxMaster, fxVol, setFxVol, fxPress, fxRelease, fxOpen, setFxOpen,
      fxMode, setFxMode, mixFx, bpm: Math.round(60 / beat),
    }}>
      {children}
    </PlayerCtx.Provider>
  );
};

export const usePlayer = () => useContext(PlayerCtx);
